// src/utils/documentoUtils.js

export const documentoRegras = {
  CPF: {
    tamanho: 11,
    placeholder: "000.000.000-00",
    mascara: "###.###.###-##",
    label: "CPF",
    regex: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
    mensagemErro: "CPF inválido (11 dígitos necessários)"
  },
  CNPJ: {
    tamanho: 14,
    placeholder: "AA.AAA.AAA/AAAA-00",
    mascara: "XX.XXX.XXX/XXXX-##",
    label: "CNPJ",
    regex: /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-\d{2}$/,
    mensagemErro: "CNPJ inválido (14 caracteres necessários)"
  },
  CNH: {
    tamanho: 11,
    placeholder: "00000000000",
    mascara: "###########",
    label: "CNH",
    regex: /^\d{11}$/,
    mensagemErro: "CNH inválida (11 dígitos necessários)"
  },
  RNE: {
    tamanho: 9,
    placeholder: "000000000",
    mascara: "#########",
    label: "RNE",
    regex: /^\d{9}$/,
    mensagemErro: "RNE inválido (9 dígitos necessários)"
  },
  ID: {
    tamanho: 2,
    placeholder: "000000000",
    mascara: "NNNNNNNNNNNNNNN",
    label: "ID",
    regex: /^[A-Za-z0-9]{2,15}$/,
    mensagemErro: "ID inválido (2 dígitos necessários)"
  }
};

export const documentoOpcoes = Object.keys(documentoRegras).map(key => ({
  valor: key,
  texto: documentoRegras[key].label
}));

export const validarDocumento = (documento, tipo) => {
  const tipoValido = tipo && documentoRegras[tipo] ? tipo : 'CPF';

  if (!documento || documento.trim() === '') {
    return { valido: false, mensagem: 'Campo obrigatório' };
  }

  const regra = documentoRegras[tipoValido];
  const valor = tipoValido === 'CNPJ' ? documento.toUpperCase() : documento;
  const valido = regra.regex.test(valor);

  return {
    valido,
    mensagem: valido ? '' : regra.mensagemErro
  };
};

export const removerMascaraDocumento = (documento) => {
  return documento ? documento.replace(/[^\d]/g, '') : '';
};

export const removerMascaraCNPJ = (cnpj) => {
  return cnpj ? String(cnpj).replace(/[^A-Za-z0-9]/g, '').toUpperCase() : '';
};

export const obterMascaraDocumento = (tipo) => {
  if (!tipo || !documentoRegras[tipo]) {
    return null;
  }

  if (tipo === 'ID' || tipo === 'CNPJ') {
    return {
      mask: documentoRegras[tipo].mascara,
      tokens: {
        '#': { pattern: /\d/ },
        X: { pattern: /[A-Za-z0-9]/, transform: v => v.toLocaleUpperCase() },
        N: { pattern: /[A-Za-z0-9]/ }
      }
    };
  }

  const m = documentoRegras[tipo].mascara;
  return m && m.trim() ? m : null;
};

export const obterPlaceholderDocumento = (tipo) => {
  const tipoValido = tipo && documentoRegras[tipo] ? tipo : 'CPF';
  return documentoRegras[tipoValido].placeholder;
};

export const obterLabelDocumento = (tipo) => {
  const tipoValido = tipo && documentoRegras[tipo] ? tipo : 'CPF';
  return documentoRegras[tipoValido].label;
};

export const validarCPF = (cpf) => {
  const cpfLimpo = removerMascaraDocumento(cpf);

  if (!cpfLimpo || cpfLimpo.length !== 11) {
    return false;
  }

  if (/^(\d)\1+$/.test(cpfLimpo)) {
    return false;
  }

  let soma = 0;
  let resto;

  for (let i = 1; i <= 9; i++) {
    soma = soma + parseInt(cpfLimpo.substring(i - 1, i)) * (11 - i);
  }
  resto = (soma * 10) % 11;

  if ((resto === 10) || (resto === 11)) {
    resto = 0;
  }
  if (resto !== parseInt(cpfLimpo.substring(9, 10))) {
    return false;
  }

  soma = 0;
  for (let i = 1; i <= 10; i++) {
    soma = soma + parseInt(cpfLimpo.substring(i - 1, i)) * (12 - i);
  }
  resto = (soma * 10) % 11;

  if ((resto === 10) || (resto === 11)) {
    resto = 0;
  }
  if (resto !== parseInt(cpfLimpo.substring(10, 11))) {
    return false;
  }

  return true;
};

export const validarCNPJ = (cnpj) => {
  const cnpjLimpo = removerMascaraCNPJ(cnpj);

  if (!cnpjLimpo || cnpjLimpo.length !== 14) {
    return false;
  }
  if (/^([A-Z0-9])\1+$/.test(cnpjLimpo)) {
    return false;
  }
  if (!/^\d{2}$/.test(cnpjLimpo.substring(12))) {
    return false;
  }

  const valorChar = ch => ch.charCodeAt(0) - 48;
  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let soma1 = 0;
  for (let i = 0; i < 12; i++) {
    soma1 += valorChar(cnpjLimpo.charAt(i)) * pesos1[i];
  }
  const dv1 = soma1 % 11 < 2 ? 0 : 11 - (soma1 % 11);
  if (dv1 !== parseInt(cnpjLimpo.charAt(12), 10)) return false;

  let soma2 = 0;
  for (let j = 0; j < 13; j++) {
    soma2 += valorChar(cnpjLimpo.charAt(j)) * pesos2[j];
  }
  const dv2 = soma2 % 11 < 2 ? 0 : 11 - (soma2 % 11);
  return dv2 === parseInt(cnpjLimpo.charAt(13), 10);
};

export const validarDocumentoCompleto = (documento, tipo) => {
  const formatoValidacao = validarDocumento(documento, tipo);
  if (!formatoValidacao.valido) {
    return formatoValidacao;
  }

  if (tipo === 'CPF') {
    const cpfValido = validarCPF(documento);
    return {
      valido: cpfValido,
      mensagem: cpfValido ? '' : 'CPF inválido (dígitos verificadores incorretos)'
    };
  }

  if (tipo === 'CNPJ') {
    const cnpjValido = validarCNPJ(documento);
    return {
      valido: cnpjValido,
      mensagem: cnpjValido ? '' : 'CNPJ inválido (dígitos verificadores incorretos)'
    };
  }

  return formatoValidacao;
};

export const substituirLabelDocumento = (html, tipoDocumento) => {
  if (!html || typeof html !== "string") return html;
  if (!tipoDocumento) return html;

  const regra = documentoRegras[tipoDocumento];
  const label = regra?.label || tipoDocumento;

  if (!label || label === "CPF") return html;

  return html.replace(/CPF:/g, `${label}:`);
};

const normalizarNomeEmail = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const resolverTipoDocumentoAssinante = ({
  nomeAssinante,
  emailAssinante,
  listaProcuradoresPJ,
  pfInfoBasicas,
}) => {
  const nomeNorm = normalizarNomeEmail(nomeAssinante);
  const emailNorm = normalizarNomeEmail(emailAssinante);
  if ((nomeNorm || emailNorm) && Array.isArray(listaProcuradoresPJ)) {
    const encontrado = listaProcuradoresPJ.find((a) => {
      if (!a || !a.TipoDocumento) return false;
      if (nomeNorm && normalizarNomeEmail(a.Nome) === nomeNorm) return true;
      if (emailNorm && normalizarNomeEmail(a.Email) === emailNorm) return true;
      return false;
    });
    if (encontrado) return encontrado.TipoDocumento;
  }
  return pfInfoBasicas?.TipoDocumento || "CPF";
};

export const formatarConteudoTermos = (conteudoTermos, tipoDocumento) => {
  const comLinks = (conteudoTermos || "").replace(
    /<a href/g,
    '<a target="_blank" href',
  );
  return substituirLabelDocumento(comLinks, tipoDocumento);
};

export const obterConteudoTermos = ({
  templateId,
  documentosCliente,
  documentosPadrao,
}) => {
  if (!(templateId > 0)) return "";

  const listaCliente = Array.isArray(documentosCliente)
    ? documentosCliente
    : [];
  const listaPadrao = Array.isArray(documentosPadrao) ? documentosPadrao : [];
  const documentoCliente = listaCliente.find(
    (documento) => documento.TemplateId == templateId,
  );
  const documentoPadrao = listaPadrao.find(
    (documento) => documento.TemplateId == templateId,
  );

  return documentoCliente?.Texto || documentoPadrao?.Texto || "";
};

export default {
  documentoRegras,
  documentoOpcoes,
  validarDocumento,
  removerMascaraDocumento,
  removerMascaraCNPJ,
  obterMascaraDocumento,
  obterPlaceholderDocumento,
  obterLabelDocumento,
  validarCPF,
  validarCNPJ,
  validarDocumentoCompleto,
  substituirLabelDocumento,
  resolverTipoDocumentoAssinante,
  formatarConteudoTermos,
  obterConteudoTermos,
};
